import { Link } from '@tanstack/react-router';
import { ArrowLeft, Menu } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { BrandMark } from '@nucleus-commerce/admin-kit/layout';
import { PermissionsProvider } from '@nucleus-commerce/admin-kit/permissions';
import { ThemeToggle } from '@nucleus-commerce/admin-kit/theme';
import { Avatar, AvatarFallback } from '@nucleus-commerce/ui/components/avatar';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Separator } from '@nucleus-commerce/ui/components/separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@nucleus-commerce/ui/components/sheet';

import { session, type Me } from '@/shared/session/session';

import { landsOnDocs } from '../lookup';

import { DocsSearch } from './docs-search';
import { DocsRailTargetContext } from './docs-shell-context';
import { DocsSidebar } from './docs-sidebar';
import '../docs.css';

function initials(name: string) {
  return name
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/** `/org` sends a developer straight back here, so their way out is the store's API keys. */
function BackToAdmin({ me }: { me: Me | null }) {
  const { t } = useTranslation('docs');
  const content = (
    <>
      <ArrowLeft aria-hidden />
      <span className="hidden sm:inline">{t('shell.backToAdmin')}</span>
      <span className="sr-only sm:hidden">{t('shell.backToAdmin')}</span>
    </>
  );
  const storeId = me?.stores[0]?.id;
  if (me && landsOnDocs(me)) {
    return (
      <Button asChild variant="ghost" size="sm">
        {storeId ? (
          <Link to="/s/$storeId/settings/api-keys" params={{ storeId }}>
            {content}
          </Link>
        ) : (
          <Link to="/org/stores">{content}</Link>
        )}
      </Button>
    );
  }
  return (
    <Button asChild variant="ghost" size="sm">
      <Link to="/org">{content}</Link>
    </Button>
  );
}

/**
 * The docs frame (plan 6.2): top bar, guides and reference sidebar, content, and a right rail
 * that pages fill through `DocsRail`. Below `lg` the sidebar and the rail are sheets.
 */
export function DocsShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation('docs');
  const { me } = session.useSession();
  const [navOpen, setNavOpen] = useState(false);
  const [railTarget, setRailTarget] = useState<HTMLElement | null>(null);

  return (
    <PermissionsProvider permissions={me?.permissions ?? []}>
      <DocsRailTargetContext.Provider value={railTarget}>
        <div className="bg-background text-foreground min-h-svh">
          <header className="bg-background/95 supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20 flex h-14 items-center gap-2 border-b px-4 backdrop-blur md:px-6">
            <Button
              variant="ghost"
              size="icon"
              className="-ml-1 lg:hidden"
              aria-label={t('shell.openNavigation')}
              onClick={() => setNavOpen(true)}
            >
              <Menu aria-hidden />
            </Button>
            <BackToAdmin me={me ?? null} />
            <Separator orientation="vertical" className="mx-1 h-4" />
            <Link to="/docs" className="flex min-w-0 items-center gap-2">
              <BrandMark size={20} />
              <span className="truncate text-[15px] font-semibold tracking-tight">
                {t('shell.title')}
              </span>
            </Link>
            <div className="flex-1" />
            <DocsSearch />
            <ThemeToggle label={t('common:theme.toggle')} />
            {me ? (
              <Avatar className="size-8 rounded-lg" title={`${me.user.name} · ${me.user.email}`}>
                <AvatarFallback className="rounded-lg text-xs">
                  {initials(me.user.name)}
                </AvatarFallback>
              </Avatar>
            ) : null}
          </header>

          <div className="mx-auto flex w-full max-w-screen-2xl">
            <aside className="sticky top-14 hidden h-[calc(100svh-3.5rem)] w-64 shrink-0 border-r lg:block">
              <DocsSidebar />
            </aside>
            <main className="min-w-0 flex-1 px-4 py-8 md:px-10 lg:py-10">{children}</main>
            <aside
              ref={setRailTarget}
              aria-label={t('rail.label')}
              className="sticky top-14 hidden h-[calc(100svh-3.5rem)] shrink-0 overflow-y-auto border-l lg:[&:not(:empty)]:block"
            />
          </div>

          <Sheet open={navOpen} onOpenChange={setNavOpen}>
            <SheetContent side="left" className="w-72 p-0" aria-describedby={undefined}>
              <SheetHeader className="border-b">
                <SheetTitle>{t('shell.navigation')}</SheetTitle>
              </SheetHeader>
              <div className="min-h-0 flex-1">
                <DocsSidebar onNavigate={() => setNavOpen(false)} />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </DocsRailTargetContext.Provider>
    </PermissionsProvider>
  );
}
